import { deviceDetector, ospThemes } from "@ospweb/sdk-ui";
import { pubsub } from "utils";
import { useMemoizedFn } from "ahooks";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";
import EdgenDrawerCallable from "../components/EdgenDrawerCallable";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { ChatStore } from "@store/modules/chat";
import { Question } from "../components/Sender/QuestionItem";
import { useIsPc } from "./useIsPc";
import {
  ChatModalCallable,
  type ChatModalCallableParams,
  type IChatInfo,
} from "@components/ChatModal";
import { getBridge } from "@/src/lib/bridge/bridge";
import { isInNativeChatShellModal } from "@/src/lib/bridge/shellAppNavigation";
import { closeActiveChatOverlay } from "@utils/chatUtils/closeActiveChatOverlay";
import { toJsonSafe } from "@utils/jsonSafe";

export interface UseOpenChatModalParams {
  /**
   * @description 自定义样式配置
   * @type ChatModalCallableParams['styles']
   * @required false
   */
  customStyles?: ChatModalCallableParams["styles"];
  /**
   * @description 自定义 zIndex
   * @type number
   * @required false
   * @default 101
   */
  zIndex?: number;
  /**
   * @description 关闭弹窗时的回调
   * @type () => void
   * @required false
   */
  onClose?: () => void;
  /**
   * @description 不走壳导航，直接打开网页弹窗
   * @type boolean
   * @default false
   */
  skipShellNavigation?: boolean;
}

/**
 * @description 打开聊天弹窗的自定义 Hook
 * @param {UseOpenChatModalParams} config - Hook 配置参数（可选）
 * @returns {Function} handleOpenChatModal - 打开聊天弹窗的函数
 *
 * @example
 * ```tsx
 * // 基础使用（无需配置）
 * const handleOpenChatModal = useOpenChatModal();
 *
 * // 打开空弹窗
 * await handleOpenChatModal();
 *
 * // 带问题打开
 * await handleOpenChatModal({ question: "你好" });
 *
 * // 带完整参数打开
 * await handleOpenChatModal({
 *   question: "问题",
 *   sign: "xxx",
 *   cacheQuestion: "yyy"
 * });
 *
 * // 带自定义配置使用
 * const handleOpenChatModal = useOpenChatModal({
 *   zIndex: 200,
 *   customStyles: {
 *     wrapper: { height: "80%" }
 *   },
 *   onClose: () => console.log("弹窗已关闭")
 * });
 * ```
 */
export const useOpenChatModalWithClose: (config?: UseOpenChatModalParams) => {
  handleOpenChatModal: (
    chatInfo?: IChatInfo & { questions?: Question[] },
  ) => Promise<void>;
  onCloseClick: () => void;
} = (config?: UseOpenChatModalParams) => {
  const {
    customStyles,
    zIndex = deviceDetector.isMobile() ? 820 : 101,
    onClose,
    skipShellNavigation = false,
  } = config || {};
  const [isPC] = useIsPc();
  /**
   * 将聊天信息转换为 JSON 安全的对象
   * @param chatInfo - 聊天信息
   * @returns JSON 安全的对象
   */
  const sanitizeChatInfo = (
    chatInfo?: IChatInfo & { questions?: Question[] },
  ): Record<string, unknown> | undefined => {
    if (!chatInfo) {
      return undefined;
    }
    const { afterCreate, customParams, questions, ...rest } = chatInfo;
    const safeCustomParams = toJsonSafe(customParams);
    const safeQuestions = toJsonSafe(questions) as Question[] | undefined;
    return {
      ...rest,
      ...(safeCustomParams ? { customParams: safeCustomParams } : {}),
      ...(safeQuestions ? { questions: safeQuestions } : {}),
    };
  };

  const onCloseClick = useMemoizedFn(() => {
    closeActiveChatOverlay({ onClosed: onClose });
  });

  /**
   * @description 打开聊天弹窗的函数
   * @param {IChatInfo} chatInfo - 聊天信息配置（可选）
   * @param {string} chatInfo.question - 问题文本
   * @param {string} chatInfo.cacheQuestion - 缓存的问题
   * @param {string} chatInfo.sign - 签名参数
   */
  const handleOpenChatModal = useMemoizedFn(
    async (chatInfo?: IChatInfo & { questions?: Question[] }) => {
      const { questions, ...restChatInfo } = chatInfo || {};

      if (restChatInfo.autoRenew) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      }
      // H5 端特有的属性
      const h5Attr = !isPC
        ? {
            draggable: false,
            initDistance: 0,
          }
        : {};

      let chatInfoParams = undefined;
      if (
        chatInfo?.question ||
        chatInfo?.shareId ||
        restChatInfo?.sign ||
        restChatInfo?.secondOpinion
      ) {
        chatInfoParams = restChatInfo;
      }

      const updateChatModalParams = {
        isOpen: true,
        chatInfo: chatInfoParams,
      };
      if (!skipShellNavigation && isInNativeChatShellModal()) {
        const payload = sanitizeChatInfo({
          ...(chatInfoParams as IChatInfo | undefined),
          questions,
        });
        getBridge().presentShellChat(payload);
        return;
      }

      const chatModalParams = {
        ...updateChatModalParams,
        questions,
        placement: isPC ? "right" : "bottom",
        styles: {
          wrapper: {
            ...customStyles?.wrapper,
          },
          body: {
            padding: "0",
            overflow: "hidden",
            position: isPC ? undefined : "relative",
            overflowX: isPC ? undefined : "hidden",
            backgroundColor: isPC
              ? ospThemes.colors.background_solid_tertiary
              : ospThemes.colors.background_solid_secondary,
            ...customStyles?.body,
          },
          content: {
            borderRadius: "0",
          },
        },
        zIndex,
        destroyOnClose: false,
        onClose: onCloseClick,
        ...h5Attr,
      } as any;

      // 发送消息，更新入口位置。
      pubsub.emit(pubSubKeyMap.CHAT_DRAWER_STATE_CHANGE, true);

      // 如果loading的过程中，创建过弹窗，则直接更新，打开当前弹窗。
      if (ChatStore.hasChatModal && ChatStore.loading) {
        await EdgenDrawerCallable.update(updateChatModalParams);
        return;
      }
      ChatStore.hasChatModal = true;
      await ChatModalCallable(chatModalParams);
    },
  );

  return { handleOpenChatModal, onCloseClick };
};

export const useOpenChatModal = (config?: UseOpenChatModalParams) => {
  const { handleOpenChatModal } = useOpenChatModalWithClose(config);
  return handleOpenChatModal;
};
